/**
 * ContextMenu — lightweight right-click context menu with keyboard nav.
 *
 * Usage:
 *   const menu = useContextMenu();
 *   <div onContextMenu={menu.handler}>...</div>
 *   <ContextMenu {...menu.props} items={[...]} />
 */

import { ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

/* ── Types ── */

export interface ContextMenuItem {
    /** Unique key */
    key: string;
    /** Display label */
    label: string;
    /** lucide-react icon element (optional) */
    icon?: React.ReactNode;
    /** Keyboard shortcut hint (display only) */
    shortcut?: string;
    /** Whether item is disabled */
    disabled?: boolean;
    /** Separator line before this item */
    separator?: boolean;
    /** Click handler */
    onClick?: () => unknown;
    /** Nested submenu items */
    children?: ContextMenuItem[];
    /** Preview handler, usually driven by hover/focus without committing selection */
    onHover?: () => void;
    /** Clear preview handler when the item is no longer previewed */
    onHoverEnd?: () => void;
}

export interface ContextMenuProps {
    open: boolean;
    x: number;
    y: number;
    items: ContextMenuItem[];
    onClose: () => void;
}

/* ── Hook ── */

export function useContextMenu() {
    const [state, setState] = useState({ open: false, x: 0, y: 0 });

    const handler = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setState({ open: true, x: e.clientX, y: e.clientY });
    }, []);

    const close = useCallback(() => {
        setState((s) => ({ ...s, open: false }));
    }, []);

    return {
        handler,
        props: { open: state.open, x: state.x, y: state.y, onClose: close },
    };
}

/* ── Component ── */

export function ContextMenu({ open, x, y, items, onClose }: ContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const [focusIdx, setFocusIdx] = useState(-1);
    const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null);
    const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
    // Track whether focus came from keyboard (true) or mouse (false)
    const focusSource = useRef<"keyboard" | "mouse">("mouse");
    // Position is computed synchronously on open via a hidden pre-measure pass,
    // so the menu never "flies in" from a wrong position.
    const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

    // Adjust position to stay within viewport
    useEffect(() => {
        if (!open) {
            setPosition(null);
            setActiveSubmenuKey(null);
            setHoveredItemKey(null);
            return;
        }
        setFocusIdx(-1);
        setActiveSubmenuKey(null);
        setHoveredItemKey(null);
        // Wait one frame for the menu to render so we can measure it
        requestAnimationFrame(() => {
            const el = menuRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            let nx = x;
            let ny = y;
            if (x + rect.width > window.innerWidth - 8) {
                nx = window.innerWidth - rect.width - 8;
            }
            if (y + rect.height > window.innerHeight - 8) {
                ny = window.innerHeight - rect.height - 8;
            }
            setPosition({ x: Math.max(4, nx), y: Math.max(4, ny) });
        });
    }, [open, x, y]);

    // Close on outside click or Escape
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }
            const enabledItems = items.filter((i) => !i.disabled);
            if (e.key === "ArrowDown") {
                e.preventDefault();
                focusSource.current = "keyboard";
                setFocusIdx((prev) => {
                    const next = prev + 1;
                    return next >= enabledItems.length ? 0 : next;
                });
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                focusSource.current = "keyboard";
                setFocusIdx((prev) => {
                    const next = prev - 1;
                    return next < 0 ? enabledItems.length - 1 : next;
                });
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (focusIdx >= 0 && focusIdx < enabledItems.length) {
                    const item = enabledItems[focusIdx];
                    if (item.children?.some((child) => !child.disabled)) {
                        setActiveSubmenuKey(item.key);
                        return;
                    }
                    item.onClick?.();
                    onClose();
                }
            } else if (e.key === "ArrowRight") {
                if (focusIdx >= 0 && focusIdx < enabledItems.length) {
                    const item = enabledItems[focusIdx];
                    if (item.children?.some((child) => !child.disabled)) {
                        e.preventDefault();
                        setActiveSubmenuKey(item.key);
                    }
                }
            }
        };
        document.addEventListener("mousedown", handleClick, true);
        document.addEventListener("keydown", handleKey, true);
        return () => {
            document.removeEventListener("mousedown", handleClick, true);
            document.removeEventListener("keydown", handleKey, true);
        };
    }, [open, items, focusIdx, onClose]);

    if (!open || items.length === 0) return null;

    // Map enabled items to index for keyboard nav highlight
    let enabledIdx = 0;

    const renderItems = (menuItems: ContextMenuItem[], level = 0) =>
        menuItems.map((item) => {
            const hasChildren = item.children?.some((child) => !child.disabled) ?? false;
            if (item.disabled) {
                return (
                    <div key={item.key} className="relative">
                        {item.separator && (
                            <div className="h-px bg-border mx-1.5 my-1" />
                        )}
                        <div className="flex items-center gap-2 px-2.5 py-1.5 text-muted-foreground/50 cursor-default select-none">
                            {item.icon && (
                                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                                    {item.icon}
                                </span>
                            )}
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.shortcut && (
                                <span className="text-[10px] text-muted-foreground/30 ml-3">
                                    {item.shortcut}
                                </span>
                            )}
                            {hasChildren && <ChevronRight size={12} />}
                        </div>
                    </div>
                );
            }

            const idx = level === 0 ? enabledIdx++ : -1;
            const isFocused = level === 0 && idx === focusIdx;
            const submenuOpen = hasChildren && activeSubmenuKey === item.key;
            const isHovered = hoveredItemKey === item.key;

            return (
                <div
                    key={item.key}
                    className="relative"
                    onMouseEnter={() => {
                        if (hasChildren) setActiveSubmenuKey(item.key);
                    }}
                    onMouseLeave={() => {
                        if (hasChildren) setActiveSubmenuKey(null);
                    }}
                >
                    {item.separator && (
                        <div className="h-px bg-border mx-1.5 my-1" />
                    )}
                    <button
                        type="button"
                        className={cn(
                            "flex items-center gap-2 w-[calc(100%-4px)] text-left px-2.5 py-1.5 cursor-default select-none rounded-sm mx-0.5",
                            isFocused || submenuOpen || isHovered
                                ? "bg-accent text-accent-foreground"
                                : "text-foreground",
                        )}
                        onMouseEnter={() => {
                            focusSource.current = "mouse";
                            setHoveredItemKey(item.key);
                            if (level === 0) setFocusIdx(idx);
                            if (hasChildren) {
                                setActiveSubmenuKey(item.key);
                            } else if (level === 0) {
                                setActiveSubmenuKey(null);
                            }
                            item.onHover?.();
                        }}
                        onMouseLeave={() => {
                            if (focusSource.current === "mouse" && level === 0) {
                                setFocusIdx(-1);
                            }
                            setHoveredItemKey((current) => current === item.key ? null : current);
                            item.onHoverEnd?.();
                        }}
                        onClick={(event) => {
                            if (hasChildren) {
                                event.preventDefault();
                                setActiveSubmenuKey(item.key);
                                return;
                            }
                            item.onClick?.();
                            onClose();
                        }}
                    >
                        {item.icon && (
                            <span className="w-4 h-4 flex items-center justify-center shrink-0">
                                {item.icon}
                            </span>
                        )}
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.shortcut && (
                            <span className="text-[10px] text-muted-foreground/50 ml-3">
                                {item.shortcut}
                            </span>
                        )}
                        {hasChildren && <ChevronRight size={12} />}
                    </button>
                    {submenuOpen && item.children && (
                        <div className="absolute left-[calc(100%-2px)] top-[-4px] min-w-[190px] max-w-[280px] bg-popover border border-border rounded-lg shadow-lg py-1">
                            {renderItems(item.children, level + 1)}
                        </div>
                    )}
                </div>
            );
        });

    return (
        <div
            ref={menuRef}
            className="fixed z-[9999] min-w-[180px] max-w-[280px] bg-popover border border-border rounded-lg shadow-lg py-1 text-xs"
            style={position
                ? { left: position.x, top: position.y, opacity: 1 }
                : { left: x, top: y, opacity: 0, pointerEvents: "none" as const }
            }
        >
            {renderItems(items)}
        </div>
    );
}
